<!--

  @copyright Copyright (c) Open-Xchange GmbH, Germany <info@open-xchange.com>
  @license AGPL-3.0

  This code is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as published by
  the Free Software Foundation, either version 3 of the License, or
  (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with OX App Suite. If not, see <https://www.gnu.org/licenses/agpl-3.0.txt>.

  Any use of the work other than as authorized under this license or copyright law is prohibited.

-->

<template>
  <div class="category">
    <button @click="toggle" class="btn-category" aria-expanded="true">
      <h1 class="flex-center heading-unstyled">
        <div class="icon-wrap flex-center" aria-hidden="true" title="Toggle category">
          <BaseIcon icon-name="bi/chevron-down.svg" v-show="expanded"/>
          <BaseIcon icon-name="bi/chevron-right.svg" v-show="!expanded"/>
        </div>
        <label class="category-label">
          {{ labelText }}
        </label>
      </h1>
    </button>
    <ul v-if="expanded && items.length > 0" :class="listClass">
      <template v-for="item in limitedList" :key="item.uniqueKey">
        <ItemComponent :data="item" tabindex="0" @keydown="openDetailView" @click="onClick"/>
      </template>
    </ul>
    <div v-if="expanded && items.length > 5" class="footer">
      <button class="btn btn-link" @click="showAll">{{ showAllString }}</button>
    </div>
  </div>
</template>

<script setup>
import ox from '@/ox'
import { ref, computed, onUnmounted } from 'vue'
import apps from '@/io.ox/core/api/apps'
import BaseIcon from '@/io.ox/vueComponents/base-icon.vue'
import gt from 'gettext'

// makes passing props in templates easier (one value instead of 7)
const { props } = defineProps(['props'])
const { labelText, type, items, appId, categories, applySearchFilters, ItemComponent } = props

const limitedList = computed(() => items.slice(0, 5))
const listClass = `items list-unstyled ${type}-list`

// start expanded
const expanded = ref(true)

function openDetailView (e) {
  if (e.which !== 13) return
  e.target.click()
}

async function onClick (e) {
  document.querySelectorAll('#io-ox-category-browser .selected').forEach(node => node.classList.remove('selected'))
  e.currentTarget.classList.add('selected')
}
function onPopupClosed (cid) {
  if (items.findIndex(item => item.cid === cid) === -1) return
  document.querySelector(`#io-ox-category-browser [data-cid="${cid}"]`).classList.remove('selected')
}
ox.on('popupClosed', onPopupClosed)

function toggle () {
  expanded.value = !expanded.value
}
function showAll () {
  const app = apps.get(appId)
  ox.launch(app.load).then(app => {
    // no searchView? App was not started before, wait for on show event to make sure function was called
    if (!app.searchView) {
      app.getWindow().one('show', () => {
        if (!app.searchView) return
        applySearchFilters(app.searchView, categories)
        app.searchView.submit()
      })
      return
    }
    applySearchFilters(app.searchView, categories)
    app.searchView.submit()
  })
}
onUnmounted(() => ox.off('popupClosed', onPopupClosed))

const showAllString = gt('Show all')
</script>

<style scoped lang="scss">
.category {
  background: var(--background);
  border-radius: 12px;
  padding: 8px;

  .icon-wrap {
    width: 24px;
    height: 24px;
    margin-inline-end: 6px;
    margin-left: 2px;
    position: relative;
    flex-shrink: 0;

    svg {
      width: 16px;
      height: auto;
      max-height: 24px;
      overflow: visible;
    }
  }

  .btn-category {
      padding: 0;
      width: 100%;
      height: 2rem;
      border-radius: var(--default-radius);
      border-width: 0;
      outline-style: none;
      border-color: transparent;
      background: transparent;

      &:hover {
        background-color: var(--selected-background-hover);
      }
      &:focus {
        background-color: var(--selected-background-focus);
        * { color: var(--selected-focus); }
      }

      h1 {
        justify-content: left;

        .category-label {
          cursor: pointer;
          margin-bottom: 0;
        }
      }
    }

  .items {
    margin: 0;
    overflow: hidden;
    &.contacts-list { margin-left: -2px; }
    li {
      position: relative;
      font-size: 13px;
      // same style as mail list
      &:hover { background-color: var(--selected-background-hover) }
      &:focus {
        background-color: var(--selected-background-focus-shade);
        color: var(--selected-focus);
        &:hover {background-color: var(--selected-background-focus-hover); }
      }
      &:hover,&:focus {
        outline: none;
        border-radius: var(--default-radius);
        &:not(:last-child):after { content: none; }
      }
      &.selected:not(:focus){
        background-color: var(--selected-background);
        &:hover { background-color: var(--selected-background-hover) }
        &:not(:last-child):after { content: none; }
      }

      &:not(:last-child):after {
        content: "";
        position: absolute;
        border-bottom: 1px solid var(--shade-a10);
        bottom: 0;
        width: calc(100% - 2* var(--default-radius));
        left: var(--default-radius);
      }
    }
  }

  .footer {
    border-top: 1px solid var(--shade-a10);
    margin: 0 var(--default-radius);
    display: flex;
    justify-content: end;
    .btn { padding-right: 8px; }
  }
}
</style>
